/**
 * Read a picked or dropped FOLDER into `{ rootName, files: [{ path, text }] }`
 * for `parseImportFolder`, with web APIs only (O2): `<input webkitdirectory>`
 * gives a FileList with `webkitRelativePath`; a drop gives
 * `FileSystemDirectoryEntry`s. No Tauri import and no fs grant — the webview
 * can read only what the user handed it.
 *
 * Every rule is applied to PATHS before any file is read:
 *  - a path segment starting with `.` (so `.env`, `.git`) or `node_modules`
 *    is never read — Bruno's `.env` holds real secrets, and Apiboo never
 *    reads it by design;
 *  - the collection's own `ignore` list (bruno.json / opencollection.yml);
 *  - only `bruno.json`, `*.bru`, `*.yml`, `*.yaml`;
 *  - at most 5 000 files, 25 MB in total, 16 directories deep — over any cap
 *    the whole read fails with a readable message and NOTHING is imported.
 *
 * Gotcha: a drop's `DataTransferItem.webkitGetAsEntry()` must be called
 * synchronously inside the drop handler — the item list is dead once the
 * handler returns. `readFolderFromDropItems` does that part before its first
 * `await`; callers may also take the entry themselves (`getDroppedDirectory`).
 */
import { loadImportYaml } from '../safeYaml';

export const MAX_FOLDER_FILES = 5000;
export const MAX_FOLDER_BYTES = 25 * 1024 * 1024;
export const MAX_FOLDER_DEPTH = 16;

const READABLE = /(^|\/)bruno\.json$|\.(bru|ya?ml)$/i;
const MANIFEST = /(^|\/)(bruno\.json|opencollection\.ya?ml)$/i;

export class FolderReadError extends Error {}

const segmentsOf = (path) => String(path || '').replace(/\\/g, '/').split('/').filter(Boolean);

/** Paths that are never read, whatever the collection says. */
export function isAlwaysSkipped(relPath) {
  return segmentsOf(relPath).some((seg) => seg.startsWith('.') || seg === 'node_modules');
}

function ignoreMatcher(entries) {
  const list = (Array.isArray(entries) ? entries : []).map((e) => String(e || '').replace(/^\.?\/+|\/+$/g, '')).filter(Boolean);
  return (relToCollection) => {
    const segs = segmentsOf(relToCollection);
    const joined = segs.join('/');
    return list.some((e) => (e.includes('/') ? joined === e || joined.startsWith(`${e}/`) : segs.includes(e)));
  };
}

async function readText(file) {
  if (file && typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsText(file);
  });
}

/**
 * The shared core: `candidates` are `{ path, size, load: () => Promise<File|Blob> }`
 * with paths relative to the picked folder. Filters, caps, then reads.
 */
async function readCandidates(candidates, rootName) {
  const visible = candidates.filter((c) => !isAlwaysSkipped(c.path) && READABLE.test(c.path));
  for (const c of visible) {
    if (segmentsOf(c.path).length - 1 > MAX_FOLDER_DEPTH) {
      throw new FolderReadError(`That folder is nested more than ${MAX_FOLDER_DEPTH} levels deep; nothing was imported.`);
    }
  }

  // Manifests first: their `ignore` lists decide what else may be read.
  const manifests = visible.filter((c) => MANIFEST.test(c.path));
  const ignores = [];
  const out = [];
  for (const m of manifests) {
    const file = await m.load();
    const text = await readText(file);
    out.push({ path: m.path, text });
    let list = [];
    try {
      const data = /\.json$/i.test(m.path) ? JSON.parse(text) : loadImportYaml(text);
      list = data?.ignore || data?.extensions?.bruno?.ignore || [];
    } catch {
      list = [];
    }
    const dir = m.path.includes('/') ? m.path.slice(0, m.path.lastIndexOf('/')) : '';
    ignores.push({ dir, match: ignoreMatcher(list) });
  }
  const ignored = (path) =>
    ignores.some(({ dir, match }) => {
      if (dir && !path.startsWith(`${dir}/`)) return false;
      return match(dir ? path.slice(dir.length + 1) : path);
    });

  const rest = visible.filter((c) => !MANIFEST.test(c.path) && !ignored(c.path));
  if (manifests.length + rest.length > MAX_FOLDER_FILES) {
    throw new FolderReadError(`That folder has more than ${MAX_FOLDER_FILES} Bruno files; nothing was imported.`);
  }
  const knownBytes = [...manifests, ...rest].reduce((n, c) => n + (Number(c.size) || 0), 0);
  if (knownBytes > MAX_FOLDER_BYTES) {
    throw new FolderReadError('That folder holds more than 25 MB of Bruno files; nothing was imported.');
  }

  let total = out.reduce((n, f) => n + f.text.length, 0);
  for (const c of rest) {
    const file = await c.load();
    if ((Number(file?.size) || 0) + total > MAX_FOLDER_BYTES) {
      throw new FolderReadError('That folder holds more than 25 MB of Bruno files; nothing was imported.');
    }
    const text = await readText(file);
    total += text.length;
    if (total > MAX_FOLDER_BYTES) {
      throw new FolderReadError('That folder holds more than 25 MB of Bruno files; nothing was imported.');
    }
    out.push({ path: c.path, text });
  }
  return { rootName, files: out };
}

/** `<input type="file" webkitdirectory>` → files. */
export async function readFolderFromFileList(fileList) {
  const files = Array.from(fileList || []);
  const first = segmentsOf(files[0]?.webkitRelativePath || '');
  const rootName = first.length > 1 ? first[0] : '';
  const candidates = files.map((file) => {
    const segs = segmentsOf(file.webkitRelativePath || file.name);
    const path = (rootName && segs[0] === rootName ? segs.slice(1) : segs).join('/');
    return { path, size: file.size, load: async () => file };
  });
  return readCandidates(candidates, rootName);
}

const readEntriesOnce = (reader) =>
  new Promise((resolve, reject) => reader.readEntries(resolve, reject));

const fileOf = (entry) => new Promise((resolve, reject) => entry.file(resolve, reject));

/** Walk a directory entry; paths only, nothing is read here. Skipped dirs are not entered. */
async function listEntries(dirEntry, prefix, depth, acc) {
  if (depth > MAX_FOLDER_DEPTH) {
    throw new FolderReadError(`That folder is nested more than ${MAX_FOLDER_DEPTH} levels deep; nothing was imported.`);
  }
  const reader = dirEntry.createReader();
  // readEntries returns results in batches (100 in Chromium) until an empty one.
  for (;;) {
    const batch = await readEntriesOnce(reader);
    if (!batch || !batch.length) break;
    for (const entry of batch) {
      const path = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (isAlwaysSkipped(path)) continue;
      if (entry.isDirectory) {
        await listEntries(entry, path, depth + 1, acc);
      } else if (entry.isFile && READABLE.test(path)) {
        acc.push({ path, size: undefined, load: () => fileOf(entry) });
        if (acc.length > MAX_FOLDER_FILES) {
          throw new FolderReadError(`That folder has more than ${MAX_FOLDER_FILES} Bruno files; nothing was imported.`);
        }
      }
    }
  }
  return acc;
}

/** A dropped directory entry → files. */
export async function readFolderFromEntry(dirEntry) {
  const candidates = await listEntries(dirEntry, '', 0, []);
  return readCandidates(candidates, dirEntry.name || '');
}

/**
 * The directory entry of a drop, or null. Call SYNCHRONOUSLY in the drop handler.
 */
export function getDroppedDirectory(items) {
  const item = items && items.length ? items[0] : null;
  const entry = item && typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
  return entry && entry.isDirectory ? entry : null;
}

/** Convenience for callers holding the drop's items (the sync part runs before the first await). */
export function readFolderFromDropItems(items) {
  const entry = getDroppedDirectory(items);
  if (!entry) return Promise.reject(new FolderReadError('That is not a folder.'));
  return readFolderFromEntry(entry);
}
